/**
 * The words on the all-entities screen, and the three types it may create.
 *
 * OND-52. An ENTITY is OND-40's answer of 12 October 2026 — "a separate legal
 * owner; every work belongs to exactly one" — so the copy here never says
 * "account" or "profile" about one. It says owner, and it says which owner.
 */

import { ENTITY_ORG_TYPES } from "@ondo/shared";
import type { EntityOrgType } from "@ondo/shared";

/** Sentence case, as every other label in this app is. */
export const ENTITY_TYPE_LABELS: Record<EntityOrgType, string> = {
  company: "Company",
  trust: "Trust",
  foundation: "Foundation",
};

/**
 * What each type is for, in the reader's own terms.
 *
 * A trust is its own type because the owner said so on 3 October 2026 —
 * "Trustees do not register as a company" — so the hint says the thing that
 * makes the choice obvious rather than restating the label.
 */
export const ENTITY_TYPE_HINTS: Record<EntityOrgType, string> = {
  company: "A business that owns work in its own name, including an investment vehicle.",
  trust: "A family or settlement trust. Trustees do not register as a company.",
  foundation: "A charitable or private foundation.",
};

/**
 * How each creatable type reads, and the sentence that tells the reader which
 * one they want.
 *
 * Derived from `ENTITY_ORG_TYPES` rather than listed independently, so the
 * screen cannot offer a type the server refuses: the order below is the order
 * on the form, and a fourth value added to that array is a compile error here
 * until somebody writes its words.
 */
export const ENTITY_TYPE_OPTIONS: readonly {
  value: EntityOrgType;
  label: string;
  hint: string;
}[] = ENTITY_ORG_TYPES.map((value) => ({
  value,
  label: ENTITY_TYPE_LABELS[value],
  hint: ENTITY_TYPE_HINTS[value],
}));

export const ENTITIES_COPY = {
  heading: "All entities",
  loading: "Reading your entities…",
  /**
   * The screen's own sentence, and it answers the walkthrough directly: "you
   * can see all of your collection all in one place, even though the ownership
   * might be in separate entities."
   */
  intro:
    "Everything you own, in one place, across every entity you act for. Each work still belongs to exactly one legal owner — turn an entity off to take it out of the totals below.",

  entitiesHeading: "Entities you act for",
  /**
   * NAMED RATHER THAN COUNTED, and the note says why: a reader whose pictures
   * are held through three owners needs to know which, and nothing in the
   * product told them before this screen.
   */
  entitiesNote:
    "Your own record, and every entity you hold a seat at. The seat is where your access comes from — this list does not grant anything.",
  self: "You",
  selfType: "Person",
  shown: "Counted",
  hidden: "Not counted",

  holdingsHeading: "Collections",
  holdingsNote:
    "Every collection you can reach, with the entity that holds it. A collection somebody shared with you has no entity to name, because the registry does not disclose its owner.",
  holdingsEmpty: "Nothing to show. Turn an entity back on, or create a collection.",
  noEntitiesCounted: "Every entity is turned off, so there is nothing to total.",

  totalCollections: "collections",
  totalWorks: "works",
  totalEntities: "entities counted",

  createHeading: "Create an entity",
  /**
   * "AND NOBODY ELSE IS ADDED" is said because it is true and because it is
   * the thing the form cannot be asked to do: `ondo.create_entity()` takes no
   * address and no ONDO id, so there is nothing for it to resolve and no way
   * for a creation to name another person. Saying so beats a reader looking
   * for a field that is deliberately absent.
   */
  createNote:
    "A company, a trust or a foundation you own. You become its administrator and nobody else is added. Work belongs to it once you register work into a collection it owns, or move work into one.",
  /**
   * SAID ON THE FORM, because it is the one thing a reader would otherwise
   * guess wrongly. The three creatable types confer no capacity, so creating
   * one does not make you a gallery and does not open a workspace.
   */
  createLimits:
    "A gallery, estate, advisory, auction house, institution, conservator or expert is a firm somebody seats you at, and is not created here.",
  nameLabel: "Name",
  namePlaceholder: "Rennie Art Ltd",
  typeLabel: "Type",
  createAction: "Create entity",
  creating: "Creating…",
  nameRequired: "An entity needs a name.",
  createFailed: "That entity could not be created.",
} as const;

/** What a newly created entity's confirmation says, with its minted id. */
export function createdMessage(name: string, ondoId: string): string {
  return `${name} is on ONDO as ${ondoId}. It is counted below, and you can register work to it or move work into it from a collection it owns.`;
}
